/**
 * Navigation glyphs.
 * ---------------------------------------------------------------
 * One hand-drawn mark per navigation destination, in the same etched /
 * forged language as the skill-tree sigils. Every mark:
 *   • draws in `currentColor` (never a hardcoded colour), so active states,
 *     hover accents and dark theme all keep working through tokens
 *   • takes the Lucide-compatible props (`className`, `strokeWidth`) so it
 *     drops into existing nav call sites with no layout change
 *   • defaults to strokeWidth 1.5 — heavier than the 1.3 skill-tree marks,
 *     which read too light at the 20px nav size
 */
import React from 'react';
import { glyphBase, type SkillGlyph, type SkillGlyphProps } from '@/components/skilltree/svg/glyph';

const Svg: React.FC<SkillGlyphProps & { children: React.ReactNode }> = ({
  className,
  strokeWidth = 1.5,
  children,
  ...rest
}) => (
  <svg {...glyphBase} strokeWidth={strokeWidth} className={className} aria-hidden {...rest}>
    {children}
  </svg>
);

/* ─────────────────────────── You ─────────────────────────── */

/** Home — the forge hearth under its arch. */
export const HomeGlyph: SkillGlyph = (p) => (
  <Svg {...p}>
    <path d="M3.5 10.8 12 3.6l8.5 7.2" />
    <path d="M5.4 10v10.4h13.2V10" />
    <path d="M9.4 20.4v-4.6a2.6 2.6 0 0 1 5.2 0v4.6" opacity={0.75} />
  </Svg>
);

/** My Personality — a struck head profile with an inner lattice. */
export const PersonalityGlyph: SkillGlyph = (p) => (
  <Svg {...p}>
    <path d="M16.8 20.8v-3.2h1.9c1 0 1.6-.8 1.4-1.8l-.5-2.3 1.4-.8-2.1-4.2A7.8 7.8 0 0 0 4.4 9.4c0 2 .8 3.5 2 4.8v6.6z" />
    <path d="M12 7.4 15.2 11 12 14.6 8.8 11z" opacity={0.75} />
  </Svg>
);

/** TPS Domains — the four-domain diamond. */
export const DomainsGlyph: SkillGlyph = (p) => (
  <Svg {...p}>
    <path d="M12 2.6 21.4 12 12 21.4 2.6 12z" />
    <path d="M12 2.6v18.8M2.6 12h18.8" opacity={0.55} />
    <circle cx="12" cy="12" r="2.4" />
  </Svg>
);

/** Trait Cards — three struck plates, offset. */
export const CardsGlyph: SkillGlyph = (p) => (
  <Svg {...p}>
    <rect x="7.6" y="6.4" width="12.8" height="14" rx="2" />
    <path d="M5.2 8.4v10.2" opacity={0.8} />
    <path d="M2.8 10.4v6.2" opacity={0.55} />
    <path d="M11 11.4h6M11 15.4h4" opacity={0.8} />
  </Svg>
);

/** Reports — a bound folio with a wax seal. */
export const ReportsGlyph: SkillGlyph = (p) => (
  <Svg {...p}>
    <path d="M5.6 2.8h8.2l4.6 4.6v13.8H5.6z" />
    <path d="M13.6 2.8v4.8h4.8" opacity={0.8} />
    <circle cx="12" cy="14.6" r="2.8" />
    <path d="M12 17.4v3.2" opacity={0.7} />
  </Svg>
);

/** History — a notched timeline spiral. */
export const HistoryGlyph: SkillGlyph = (p) => (
  <Svg {...p}>
    <path d="M20.4 12a8.4 8.4 0 1 1-2.9-6.4" />
    <path d="M20.4 3.6v3.9h-3.9" />
    <path d="M12 7.6V12l3.2 2" />
  </Svg>
);

/** Account — a single struck profile ring. */
export const AccountGlyph: SkillGlyph = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9.2" opacity={0.55} />
    <circle cx="12" cy="9.6" r="3" />
    <path d="M6.2 19.2c1.2-2.6 3.2-3.9 5.8-3.9s4.6 1.3 5.8 3.9" />
  </Svg>
);

/* ─────────────── Practice & Assessments ─────────────── */

/** Take Assessment — a measuring dial with a struck needle. */
export const AssessGlyph: SkillGlyph = (p) => (
  <Svg {...p}>
    <path d="M3 17.6a9 9 0 1 1 18 0" />
    <path d="M12 17.6 16.4 9.8" />
    <circle cx="12" cy="17.6" r="1.6" />
    <path d="M4.6 12.6l1.8.7M12 8.6V6.8M19.4 12.6l-1.8.7" opacity={0.7} />
  </Svg>
);

/** Integral — nine ascending altitudes on one column. */
export const IntegralGlyph: SkillGlyph = (p) => (
  <Svg {...p}>
    <path d="M12 21.2V3.2" />
    <path d="M8.6 18.2h6.8M9.4 14.6h5.2M10.2 11h3.6M10.8 7.4h2.4" opacity={0.85} />
    <path d="M12 3.2l2.2 2.4M12 3.2 9.8 5.6" opacity={0.7} />
  </Svg>
);

/** Quizzes — a branching choice fork. */
export const QuizzesGlyph: SkillGlyph = (p) => (
  <Svg {...p}>
    <circle cx="5.4" cy="12" r="2.4" />
    <circle cx="18.4" cy="6.4" r="2.2" />
    <circle cx="18.4" cy="17.6" r="2.2" />
    <path d="M7.6 11.1c3.2-1 5.2-2 8.7-3.8" />
    <path d="M7.6 12.9c3.2 1 5.2 2 8.7 3.8" opacity={0.8} />
  </Svg>
);

/** IQ Test — nested matrix cells with the missing facet. */
export const IQGlyph: SkillGlyph = (p) => (
  <Svg {...p}>
    <rect x="3.2" y="3.2" width="7.2" height="7.2" rx="1.2" />
    <rect x="13.6" y="3.2" width="7.2" height="7.2" rx="1.2" opacity={0.8} />
    <rect x="3.2" y="13.6" width="7.2" height="7.2" rx="1.2" opacity={0.8} />
    <path d="M13.6 17.2h7.2M17.2 13.6v7.2" opacity={0.7} />
  </Svg>
);

/** Daily Reflection — a crescent over a still surface. */
export const ReflectGlyph: SkillGlyph = (p) => (
  <Svg {...p}>
    <path d="M15.6 2.8a7.4 7.4 0 1 0 3.4 10.8A7.4 7.4 0 0 1 15.6 2.8z" />
    <path d="M3.4 18.4c2-1.2 3.4-1.2 5.4 0s3.4 1.2 5.4 0 3.4-1.2 5.4 0" opacity={0.7} />
    <path d="M3.4 21.2c2-1.2 3.4-1.2 5.4 0" opacity={0.45} />
  </Svg>
);

/* ───────────────────────── Connect ───────────────────────── */

/** Relationships — two rings held by one bond bar. */
export const RelationshipsGlyph: SkillGlyph = (p) => (
  <Svg {...p}>
    <circle cx="8" cy="12" r="4.8" />
    <circle cx="16" cy="12" r="4.8" opacity={0.8} />
    <path d="M10.4 12h3.2" />
  </Svg>
);

/** Bookmarks — a folded ribbon. */
export const BookmarksGlyph: SkillGlyph = (p) => (
  <Svg {...p}>
    <path d="M6.4 3.2h11.2v17.6L12 16.4l-5.6 4.4z" />
    <path d="M9.4 8h5.2" opacity={0.7} />
  </Svg>
);

/** Liker — a faceted heart, cut so it never reads as Relationships. */
export const LikerGlyph: SkillGlyph = (p) => (
  <Svg {...p}>
    <path d="M12 20.6 4.2 13a4.8 4.8 0 0 1 7.8-5.4A4.8 4.8 0 0 1 19.8 13z" />
    <path d="M12 7.6v13M7.4 9.4 12 13.6l4.6-4.2" opacity={0.6} />
  </Svg>
);

/** AI Mentor — a speech facet with an inner spark. */
export const MentorGlyph: SkillGlyph = (p) => (
  <Svg {...p}>
    <path d="M4 5.4h16v10.4H9.6L5.2 19.6v-3.8H4z" />
    <path d="M12 8.4v4.4M9.8 10.6h4.4" opacity={0.8} />
    <path d="M16.4 9.2v2.6" opacity={0.6} />
  </Svg>
);

/** Career — a forged case with the rising path over it. */
export const CareerGlyph: SkillGlyph = (p) => (
  <Svg {...p}>
    <rect x="2.8" y="8" width="18.4" height="12.4" rx="2" />
    <path d="M9 8V5.6a1.6 1.6 0 0 1 1.6-1.6h2.8A1.6 1.6 0 0 1 15 5.6V8" />
    <path d="M6.4 16.2l3.2-3 2.6 2.2 4.8-4.4" opacity={0.85} />
  </Svg>
);

/** Astrology — a star aperture riding an ecliptic band. */
export const AstrologyGlyph: SkillGlyph = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" opacity={0.55} />
    <path d="M12 5.6l1.9 3.9 4.3.6-3.1 3 .8 4.3L12 15.4l-3.9 2 .8-4.3-3.1-3 4.3-.6z" />
  </Svg>
);

/** Community — the Discord face, kept recognizable on purpose. */
export const CommunityGlyph: SkillGlyph = (p) => (
  <Svg {...p}>
    <path d="M8.6 4.6 7.8 6A13 13 0 0 0 4.4 7.4C2.7 10.4 2.4 14 3.4 16.8c1.3 1 2.9 1.7 4.6 2l.9-1.7a11 11 0 0 0 6.2 0l.9 1.7c1.7-.3 3.3-1 4.6-2 1-2.8.7-6.4-1-9.4A13 13 0 0 0 16.2 6l-.8-1.4a12 12 0 0 0-6.8 0z" />
    <circle cx="9.2" cy="13" r="1.3" />
    <circle cx="14.8" cy="13" r="1.3" />
  </Svg>
);

/* ─────────────────────────── Coins ─────────────────────────── */

/** Wallet & Earn — a struck coin held in a purse arc. */
export const WalletGlyph: SkillGlyph = (p) => (
  <Svg {...p}>
    <path d="M3.2 7.6h14.4a3.2 3.2 0 0 1 3.2 3.2v6.4a3.2 3.2 0 0 1-3.2 3.2H3.2z" />
    <path d="M3.2 7.6V4.4l11 1.9" opacity={0.75} />
    <circle cx="16.2" cy="14" r="1.6" />
  </Svg>
);

/** Coin Shop — a coin over a market arch. */
export const ShopGlyph: SkillGlyph = (p) => (
  <Svg {...p}>
    <path d="M3.6 8.4h16.8l-1.4 12H5z" />
    <path d="M8.4 8.4V6a3.6 3.6 0 0 1 7.2 0v2.4" opacity={0.8} />
    <circle cx="12" cy="14.4" r="2.4" />
    <path d="M12 13v2.8" opacity={0.7} />
  </Svg>
);

/* ─────────────────────────── Learn ─────────────────────────── */

/** Science — an open folio with an orbit struck through it. */
export const ScienceGlyph: SkillGlyph = (p) => (
  <Svg {...p}>
    <path d="M12 6.4C10.2 4.8 7.6 4.2 3.6 4.4v13.4c4-.2 6.6.4 8.4 2 1.8-1.6 4.4-2.2 8.4-2V4.4c-4-.2-6.6.4-8.4 2z" />
    <path d="M12 6.4v13.4" opacity={0.6} />
    <circle cx="12" cy="12.6" r="2.2" opacity={0.85} />
  </Svg>
);

/* ─────────────────────────── System ─────────────────────────── */

/** Admin — a shield with a forged keyway. */
export const AdminGlyph: SkillGlyph = (p) => (
  <Svg {...p}>
    <path d="M12 2.8 20 5.8v6c0 4.4-3.2 7.7-8 9.4-4.8-1.7-8-5-8-9.4v-6z" />
    <circle cx="12" cy="10.6" r="2.1" />
    <path d="M12 12.7v4M12 15h2" opacity={0.8} />
  </Svg>
);

/** Subscription — a struck crown. */
export const CrownGlyph: SkillGlyph = (p) => (
  <Svg {...p}>
    <path d="M2.8 6.6 6.6 13l5.4-8 5.4 8 3.8-6.4v12.8H2.8z" />
    <path d="M2.8 17h18.4" opacity={0.6} />
  </Svg>
);

/** Leaderboard — a tiered plinth. */
export const LeaderboardGlyph: SkillGlyph = (p) => (
  <Svg {...p}>
    <path d="M3.2 20.4h17.6" />
    <rect x="4.4" y="12.8" width="4.4" height="7.6" />
    <rect x="9.8" y="8" width="4.4" height="12.4" opacity={0.85} />
    <rect x="15.2" y="15.2" width="4.4" height="5.2" opacity={0.7} />
  </Svg>
);

/** Commons / people — three anchored points. */
export const PeopleGlyph: SkillGlyph = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="6.6" r="2.8" />
    <circle cx="5.4" cy="15.4" r="2.4" opacity={0.85} />
    <circle cx="18.6" cy="15.4" r="2.4" opacity={0.85} />
    <path d="M10.2 8.9 7.2 13.4M13.8 8.9l3 4.5M7.8 16.4h8.4" opacity={0.7} />
  </Svg>
);

/** Explore — a faceted globe. */
export const ExploreGlyph: SkillGlyph = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3.2 12h17.6" opacity={0.75} />
    <path d="M12 3c2.8 2.6 2.8 15.4 0 18-2.8-2.6-2.8-15.4 0-18z" opacity={0.75} />
  </Svg>
);

/** Cognitive Report Card — a graded ledger with a struck mark. */
export const ReportCardGlyph: SkillGlyph = (p) => (
  <Svg {...p}>
    <rect x="4" y="3.2" width="16" height="17.6" rx="1.6" />
    <path d="M7.4 8h5.2M7.4 12h5.2M7.4 16h3.6" opacity={0.75} />
    <path d="M15.2 9.6 16.6 6.4 18 9.6M15.6 8.6h2" />
    <path d="M15.4 14.6l1.2 1.2 2-2.4" opacity={0.85} />
  </Svg>
);
